import { parse as parseContentDisposition } from '@tinyhttp/content-disposition'
import Axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseHeaders,
  CancelTokenSource,
  RawAxiosResponseHeaders,
  ResponseType,
} from 'axios'
import { NetworkActions } from 'features/SignalR/store/ConnectionReducer'
import _ from 'lodash'
import { TelemetryService } from 'services/Telemetry'
import store from 'store/configureStore'
import { AuthService } from '../auth'
import { HttpGetMetaData } from './InternalAPI/InternalAPI'

export interface APIResponse<T> {
  records: T[]
  _meta?: {
    page: number
    pageSize: number
    totalRecords: number
    totalPages: number
  }
}

export type AppRequestError = {
  message: string
  validation?: Record<string, string>
  status: number
}

export type RequestStatus = {
  id: string
  isLoading: boolean
  error: {
    message: string
    validation?: Record<string, string>
    status: number
  }
  validation: { [validationKey: string]: string }
  progress?: number
  retries?: number
}

export type OnRequestChangeCallback = (
  requestStatus: Partial<RequestStatus>,
) => void

type RequestAllowedData<T> = Array<T> | Record<string, T> | File[] | unknown

export type Request = {
  id?: string
  relativePath: string
  onRequestChange?: OnRequestChangeCallback
  data?: unknown
  onUploadProgress?: (progress: number) => void
  params?: Record<string, unknown>
  headers?: Record<string, string>
  uri?: string
  _meta?: HttpGetMetaData<never>
  retryOnTimeout?: boolean
  /**
   * Whether to retry the request on network failure (status 0 or undefined)
   */
  retryOnFailed?: boolean
}

export const REQUEST_CANCELLED_MESSAGE = 'user cancelled'

/**
 * Detects a request that never reached the server because it was cancelled
 * client-side (navigation, unmount, superseded search) rather than because of
 * a real backend/network failure. These are expected and must not be reported
 * as errors.
 */
export const isRequestCancelled = (err: unknown): boolean => {
  if (!err) return false

  if (Axios.isCancel(err)) return true

  const message = (
    (err as { message?: string })?.message ?? ''
  ).toLowerCase()

  return (
    message === REQUEST_CANCELLED_MESSAGE ||
    message === 'canceled' ||
    message === 'cancelled' ||
    message === 'request aborted' ||
    (err as { code?: string })?.code === 'ERR_CANCELED'
  )
}

export abstract class BaseAPI {
  protected relativePath = '/'

  /**
   * Every in-flight request owns its own cancel token. Previously a single
   * shared source was reassigned per request, which meant parallel requests
   * from the same API instance aborted each other (e.g. the per-project-state
   * kanban fan-out) and `CancelRequests()` only ever cancelled the most
   * recently started one.
   */
  private inFlightSources = new Set<CancelTokenSource>()

  public CancelRequests() {
    for (const source of this.inFlightSources) {
      source.cancel(REQUEST_CANCELLED_MESSAGE)
    }
    this.inFlightSources.clear()
    this.globalOnRequestChange({ isLoading: false, progress: 0, retries: 0 })
  }

  constructor(
    protected baseAddress?: string,
    private onRequestChangeCallback?: OnRequestChangeCallback,
  ) {}

  protected GetFunctionUrl = (relativePath: string) => {
    const functionUrl =
      this.baseAddress +
      (this.relativePath === '/' ? '' : this.relativePath) +
      relativePath

    if (functionUrl.endsWith('/')) {
      return functionUrl.slice(0, -1)
    } else {
      return functionUrl
    }
  }

  public withAuthHeader = async (headers?: Record<string, string>) => {
    const _headers = { ...(headers || {}) }

    const accessToken = await AuthService.GetCurrent()?.GetAccessToken()

    if (accessToken) {
      _headers['Authorization'] = `Bearer ${accessToken}`
    }

    return _headers
  }

  private globalOnRequestChange = (requestStatus: Partial<RequestStatus>) => {
    try {
      store.dispatch(
        NetworkActions.SetLatestRequest({ request: requestStatus }),
      )
    } catch (err) {
      console.error('globalOnRequestChange error', err)
    }
  }

  private notifyRequestChanged = (
    requestStatus: Partial<RequestStatus>,
    onRequestChange: OnRequestChangeCallback,
  ) => {
    if (onRequestChange) {
      onRequestChange(requestStatus)
    } else if (this.onRequestChangeCallback) {
      this.onRequestChangeCallback(requestStatus)
    }

    this.globalOnRequestChange(requestStatus)
  }

  retries = 0
  private MAX_RETRIES = 3

  private async SendRequest<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(
    req: Request,
    config: AxiosRequestConfig,
  ): Promise<{
    data: responseDataType
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders
  }> {
    let resp: AxiosResponse<unknown>
    let currentRetries = 0

    const executeRequest = async (): Promise<{
      data: responseDataType
      headers: RawAxiosResponseHeaders | AxiosResponseHeaders
    }> => {
      const source = Axios.CancelToken.source()

      try {
        if (config.url.includes('undefined')) {
          console.warn('undefined in url', config.url)
          return null
        }

        this.inFlightSources.add(source)

        const traceHeaders =
          TelemetryService.getInstance().getCorrelationHeaders()

        if (traceHeaders) {
          config.headers = {
            ...(config.headers || {}),
            ...traceHeaders,
          }
        }

        this.notifyRequestChanged(
          {
            isLoading: true,
            id: req.id,
          },
          req.onRequestChange,
        )

        resp = await Axios({ ...config, cancelToken: source.token })

        this.notifyRequestChanged(
          {
            isLoading: false,
            id: req.id,
          },
          req.onRequestChange,
        )

        return {
          data: resp?.data as responseDataType,
          headers: resp.headers,
        }
      } catch (err) {
        const errorData = this.getErrorData(err as AxiosError<never>)

        if (isRequestCancelled(err)) {
          // Expected client-side cancellation: stop the spinner, stay silent.
          this.notifyRequestChanged(
            {
              isLoading: false,
              id: req.id,
            },
            req.onRequestChange,
          )

          return null
        }

        const retryFn = async () => {
          currentRetries++

          TelemetryService.getInstance().TrackEvent('BaseAPI:RequestRetry', {
            method: config.method,
            path: req.relativePath,
            retries: currentRetries,
          })

          if (currentRetries <= this.MAX_RETRIES) {
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * currentRetries),
            )

            return executeRequest()
          }

          return Promise.reject(errorData)
        }

        // Handle status 0 or undefined (Network Error) with retry logic
        if (
          (!errorData.status && errorData.message === 'Network Error') ||
          req.retryOnFailed
        ) {
          return await retryFn()
        }

        if (
          errorData.message.toLowerCase() === 'timeout exceeded' &&
          req.retryOnTimeout
        ) {
          return await retryFn()
        }

        console.warn('[BaseAPI]: SendRequest error', errorData)

        this.notifyRequestChanged(
          {
            isLoading: false,
            id: req.id,
            error: errorData,
          } as RequestStatus,
          req.onRequestChange,
        )

        return Promise.reject(errorData)
      } finally {
        this.inFlightSources.delete(source)
      }
    }

    return executeRequest()
  }

  protected OnRequestChangeWithId(
    operationId: string,
    req: Partial<RequestStatus>,
  ) {
    return {
      ...req,
      id: operationId,
    }
  }

  protected async GetAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(req: Request) {
    try {
      const resp = await this.SendRequest(req, {
        method: 'GET',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath),
        params: req.params,
        headers: await this.withAuthHeader(req.headers),
      })

      return resp?.data as responseDataType
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   *  GetAsync that rejects if not able to retrieve the authentication token
   * @param req request configuration
   * @returns
   */
  protected async GetAuthenticatedAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(req: Request) {
    try {
      const withAuthHeader = await this.withAuthHeader(req.headers)

      if (!withAuthHeader['Authorization']) {
        return Promise.reject({
          message: 'Authentication token not available',
          status: 401,
        })
      }

      const resp = await this.SendRequest(req, {
        method: 'GET',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath),
        params: req.params,
        headers: withAuthHeader,
      })

      return resp?.data as responseDataType
    } catch (err) {
      return Promise.reject(err)
    }
  }

  protected async GetPagedAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(req: Request) {
    try {
      const resp = await this.SendRequest(req, {
        method: 'GET',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath),
        params: {
          ...req.params,
          ...req._meta,
          orderBy: req._meta?.orderBy
            ? _.upperFirst(req._meta?.orderBy.toString()) +
              ' ' +
              (req._meta?.orderDirection || 'desc')
            : null,
          orderDirection: undefined,
        },
        headers: await this.withAuthHeader(req.headers),
      })

      return resp?.data as responseDataType
    } catch (err) {
      return Promise.reject(err)
    }
  }

  protected async GetWithResponseTypeAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(req: Request, responseType: ResponseType) {
    try {
      const resp = await this.SendRequest(req, {
        method: 'GET',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath),
        params: req.params,
        headers: await this.withAuthHeader(req.headers),
        responseType: responseType,
      })

      return resp?.data as responseDataType
    } catch (err) {
      return Promise.reject(err)
    }
  }

  protected async PutAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(
    req: Request,
    transformResponse?: (resp: {
      data: responseDataType
      headers: RawAxiosResponseHeaders | AxiosResponseHeaders
    }) => responseDataType,
  ) {
    try {
      const response = await this.SendRequest<responseDataType>(req, {
        method: 'PUT',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath || '/'),
        headers: await this.withAuthHeader(req.headers),
        data: req.data,
        params: req.params,
      })

      if (transformResponse) {
        return transformResponse(response)
      } else {
        return (
          (response?.data['result'] as responseDataType) ??
          (response?.data as responseDataType)
        )
      }
    } catch (err) {
      return Promise.reject(err)
    }
  }

  protected async PostAsync<
    responseDataType extends RequestAllowedData<responseDataType>,
  >(req: Request, transformResponse?: (resp: unknown) => responseDataType) {
    try {
      const config = {
        method: 'POST',
        url: req.uri ?? this.GetFunctionUrl(req.relativePath || '/'),
        headers: await this.withAuthHeader(req.headers),
        data: req.data,
      }

      const response = await this.SendRequest(req, config)

      if (transformResponse) {
        return transformResponse(response)
      } else {
        return response?.data as responseDataType
      }
    } catch (err) {
      // console.log('PostAsync error', err)
      return Promise.reject(err)
    }
  }

  protected async DeleteAsync(req: Request) {
    return await this.DeleteAsyncWithResp<void>(req)
  }

  protected async DeleteAsyncWithResp<T>(req: Request) {
    try {
      const resp = await this.SendRequest<T>(req, {
        method: 'DELETE',
        data: req.data,
        url: req.uri ?? this.GetFunctionUrl(req.relativePath),
        headers: await this.withAuthHeader(req.headers),
        params: req.params,
      })

      return resp?.data as T
    } catch (err) {
      return Promise.reject(err)
    }
  }

  public getErrorData(
    err: AxiosError<{
      message?: string
      detail?: string
      validation?: Record<string, string>
      Errors?: Record<string, string>
      errors?: Record<string, string>
      Title?: string
    }>,
  ): AppRequestError {
    let errMessage = err.message ?? err.toString()

    if (err.response) {
      if (err.response.status === 403) {
        errMessage = 'action not allowed'
      } else if (err.response?.data?.message) {
        errMessage = err.response?.data?.message
      } else if (err.response?.data?.detail) {
        errMessage = err.response?.data?.detail
      } else if (err.response?.data?.Title) {
        errMessage = err.response?.data?.Title
      } else if (err.response.statusText) {
        errMessage = err.response.statusText
      } else if (err.message) {
        errMessage = err.message
      } else {
        errMessage = err.toString()
      }
    }

    let validation = err.response?.data?.validation

    if (!validation) {
      if (err.response?.data?.Errors) {
        validation = err.response?.data?.Errors
      } else if (err.response?.data?.errors) {
        validation = err.response?.data?.errors
      }
    }

    const appError = {
      message: errMessage,
      status: err.response?.status,
      validation: validation,
    }

    return appError
  }

  protected async UploadWithData<T>(req: Request) {
    return await this.UploadAsync<T>(req, 'args')
  }

  private appendFileDetailFieldToFormData(
    formData: FormData,
    parameterName: string,
    index: number,
    dataObject: Record<string, string | Blob>,
    key: string,
  ) {
    const field = dataObject[key]

    if (field instanceof File) {
      formData.append(`${parameterName}[${index}].${key}`, field as File)
    } else if (field instanceof Array) {
      field.forEach((item, i) => {
        formData.append(`${parameterName}[${index}].${key}[${i}]`, item)
      })
    } else if (field instanceof Object) {
      formData.append(
        `${parameterName}[${index}].${key}`,
        JSON.stringify(field),
      )
    } else if (field) {
      formData.append(`${parameterName}[${index}].${key}`, field)
    }
  }

  private getFileUploadBody(
    data: Array<File | Record<string, unknown>>,
    parameterName = 'files',
  ) {
    let form: FormData

    if (data && data instanceof Array) {
      if (data[0] instanceof File) {
        form = new FormData()
        data.forEach((file) => form.append(parameterName, file as File))
      } else {
        form = new FormData()
        data.forEach((record, index) =>
          Object.keys(record).forEach((key) => {
            this.appendFileDetailFieldToFormData(
              form,
              parameterName,
              index,
              record as Record<string, string | Blob>,
              key,
            )
          }),
        )
      }
    }

    return form || data
  }

  protected async UploadAsync<T>(
    req: Request,
    controllerParameterName = 'geometryFiles',
  ) {
    try {
      const data = this.getFileUploadBody(
        req.data as [],
        controllerParameterName,
      )

      req.onUploadProgress && req.onUploadProgress(0)

      const response = await this.SendRequest<T>(req, {
        method: 'POST',
        url: req.uri || this.GetFunctionUrl(req.relativePath),
        data: data,
        params: req.params,
        headers: await this.withAuthHeader(req.headers),
        onUploadProgress: (progress: AxiosProgressEvent) => {
          const p =
            (progress.total > 0 &&
              progress.total >= progress.loaded &&
              (progress.loaded / progress.total) * 100) ||
            undefined
          req.onRequestChange &&
            req.onRequestChange({
              ...req,
              progress: p,
            })
          req.onUploadProgress && req.onUploadProgress(p)
        },
      })

      req.onRequestChange &&
        req.onRequestChange({
          ...req,
        })
      req.onUploadProgress && req.onUploadProgress(undefined)

      return response?.data as T
    } catch (err) {
      return Promise.reject(err)
    }
  }

  protected async DownloadBlob(req: Request) {
    const source = Axios.CancelToken.source()
    this.inFlightSources.add(source)

    try {
      req.onRequestChange &&
        req.onRequestChange({ isLoading: true, id: req.id })

      const response = await Axios.get(this.GetFunctionUrl(req.relativePath), {
        responseType: 'blob',
        headers: await this.withAuthHeader(),
        params: req.params,
        cancelToken: source.token,
      })

      return response?.data as Blob | MediaSource
    } catch (ex) {
      if (isRequestCancelled(ex)) {
        return null
      }

      console.error('DownloadBlob error', ex)
      throw ex
    } finally {
      this.inFlightSources.delete(source)
      req.onRequestChange &&
        req.onRequestChange({ isLoading: false, id: req.id })
    }
  }

  protected async DownloadAsync(req: Request, usePost = false) {
    try {
      req.onRequestChange &&
        req.onRequestChange({ isLoading: true, id: req.id })

      let response: AxiosResponse<never, never>

      if (usePost) {
        response = await Axios.post(
          this.GetFunctionUrl(req.relativePath),
          req.data,
          {
            responseType: 'arraybuffer',
            headers: await this.withAuthHeader(),
            params: req.params,
          },
        )
      } else {
        response = await Axios.get(this.GetFunctionUrl(req.relativePath), {
          responseType: 'arraybuffer',
          headers: await this.withAuthHeader(),
          params: req.params,
        })
      }

      if (!response) {
        return false
      }

      if (response.status === 204) {
        // no content
        console.warn(response)
        return false
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      let fileName = 'resource.zip'

      if (response.headers['content-disposition']) {
        const cd = parseContentDisposition(
          response.headers['content-disposition'],
        )
        fileName = cd.parameters['filename'] as string
      } else {
        console.warn('content-disposition header not found')
      }

      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      req.onRequestChange &&
        req.onRequestChange({ isLoading: false, id: req.id })

      return true
    } catch (err: any) {
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.info(err.response.data)
        console.info(err.response.status)
        console.info(err.response.headers)
      }
      req.onRequestChange &&
        req.onRequestChange({ isLoading: false, id: req.id, error: err })

      return Promise.reject(err)
    }
  }
}
